import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Checkbox } from "./components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";
import {
  DollarSign,
  TrendingUp,
  Clock,
  Shield,
  CheckCircle2,
  Mail,
  Phone,
  MessageSquare,
  Calendar,
  FileText,
  Database,
  Workflow,
  BookOpen,
  Building2,
  Scale,
  Calculator,
  Stethoscope,
  Truck,
  ShoppingCart,
  ChevronRight,
  Menu,
  X,
  Zap,
  Users,
  ArrowRight,
  Lock,
  Eye,
  FileCheck,
  Server,
  Quote,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "./context/LanguageContext";
import { translations } from "./translations";
import ScandicDevLogo from "./components/ScandicDevLogo";
import LanguageToggleLink from "./components/LanguageToggleLink";
import Seo, {
  JsonLd,
  buildFaqSchema,
  buildOrganizationSchema,
  buildWebsiteSchema,
} from "./components/Seo";
import { industryLinks, mainServiceLinks } from "./content/marketingPages";
import {
  englishHomeSeo,
  getAlternateLanguagePath,
  getHomePath,
  getLegalPath,
  homeSeo,
} from "./lib/site";

// Icon mapping for benefits
const benefitIcons = [DollarSign, TrendingUp, Clock, CheckCircle2, Zap, Workflow];

// Icon mapping for functions
const functionIcons = [Phone, Mail, MessageSquare, Calendar, FileText, Database, Workflow, BookOpen];

// Icon mapping for industries
const industryIcons = [Calculator, Scale, Building2, Stethoscope, Truck, ShoppingCart];

// Icon mapping for security
const securityIcons = [Shield, Users, Eye, Lock, FileCheck, Server];

const readRuntimeConfig = (key) => {
  if (typeof window === "undefined") {
    return "";
  }

  const globalValue =
    window.__APP_CONFIG__?.[key] ||
    window.__APP_CONFIG__?.[key.replace("REACT_APP_", "")] ||
    window[key] ||
    window[key.replace("REACT_APP_", "")];

  if (globalValue) {
    return globalValue;
  }

  const metaName = key.toLowerCase().replaceAll("_", "-");
  return document
    ?.querySelector(`meta[name="${metaName}"]`)
    ?.getAttribute("content") || "";
};

const resolveConfig = (primaryKey, fallbackKey, defaultValue = "") =>
  process.env[primaryKey] ||
  process.env[fallbackKey] ||
  readRuntimeConfig(primaryKey) ||
  readRuntimeConfig(fallbackKey) ||
  defaultValue;

function App() {
  const { language } = useLanguage();
  const location = useLocation();
  const t = translations[language];
  const homePath = getHomePath(language);
  const alternateLanguagePath = getAlternateLanguagePath(location.pathname);
  const seoConfig = language === "de" ? homeSeo : englishHomeSeo;
  const formEndpoint = resolveConfig("REACT_APP_FORM_ENDPOINT", "FORM_ENDPOINT");
  const formSubject = resolveConfig(
    "REACT_APP_FORM_SUBJECT",
    "FORM_SUBJECT",
    "ScandicDev Kontaktanfrage"
  );
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    firma: "",
    branche: "",
    prozess: "",
    consent: false,
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  const sectionRefs = useRef([]);

  // Navigation links with translations
  const navLinks = [
    { href: "#vorteile", label: t.nav.vorteile },
    { href: "#funktionen", label: t.nav.funktionen },
    { href: "#branchen", label: t.nav.branchen },
    { href: "#ablauf", label: t.nav.ablauf },
    { href: "#referenzen", label: t.nav.referenzen },
    { href: "#faq", label: t.nav.faq },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
          }
        });
      },
      { threshold: 0.1 }
    );
    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.consent) {
      return;
    }

    if (!formEndpoint) {
      setFormError(
        language === "de"
          ? "Formular-Endpunkt fehlt. Bitte REACT_APP_FORM_ENDPOINT oder FORM_ENDPOINT setzen."
          : "Form endpoint is missing. Please set REACT_APP_FORM_ENDPOINT or FORM_ENDPOINT."
      );
      return;
    }

    setFormLoading(true);
    setFormError("");
    try {
      const response = await fetch(formEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          firma: formData.firma,
          branche: formData.branche,
          prozess: formData.prozess,
          subject: formSubject,
          language,
          consent: true,
          source: window.location.href,
        }),
      });

      if (!response.ok) {
        throw new Error("Form submit failed");
      }

      setFormSubmitted(true);
      setFormData({
        name: "",
        email: "",
        firma: "",
        branche: "",
        prozess: "",
        consent: false,
      });
    } catch {
      setFormError(language === "de"
        ? "Fehler beim Senden. Bitte versuchen Sie es erneut."
        : "Failed to send. Please try again.");
    } finally {
      setFormLoading(false);
    }
  };

  const addToRefs = (el) => {
    if (el && !sectionRefs.current.includes(el)) {
      sectionRefs.current.push(el);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B]">
      <Seo
        title={seoConfig.title}
        description={seoConfig.description}
        path={location.pathname}
        language={language}
        type="website"
      />
      <JsonLd data={buildOrganizationSchema()} />
      {language === "de" ? <JsonLd data={buildWebsiteSchema()} /> : null}
      <JsonLd data={buildFaqSchema(t.faq.items)} />

      {/* Header */}
      <header className={`header-sticky ${isScrolled ? "scrolled" : ""}`} data-testid="header">
        <div className="container-main">
          <div className="flex items-center justify-between h-20 md:h-24">
            <a href={`${homePath}#top`} className="flex items-center" data-testid="logo">
              <ScandicDevLogo className="h-14 w-auto object-contain md:h-16" />
            </a>

            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <a key={link.href} href={link.href}
                  className="text-sm text-zinc-400 hover:text-white transition-colors" data-testid={`nav-${link.href.slice(1)}`}>
                  {link.label}
                </a>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <LanguageToggleLink language={language} href={alternateLanguagePath} />
              <Button asChild className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-6 btn-scale" data-testid="header-cta">
                <a href="#kontakt">{t.nav.cta}</a>
              </Button>
            </div>

            <button className="lg:hidden p-2 text-zinc-400 hover:text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`} data-testid="mobile-menu">
          <div className="p-6">
            <div className="flex justify-end mb-8">
              <button onClick={() => setMobileMenuOpen(false)} className="p-2 text-zinc-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a key={link.href} href={link.href} onClick={() => setMobileMenuOpen(false)}
                  className="text-lg text-zinc-300 hover:text-white py-2 transition-colors">
                  {link.label}
                </a>
              ))}
              <LanguageToggleLink language={language} href={alternateLanguagePath} className="mt-4 justify-center" />
              <Button asChild className="mt-2 bg-violet-600 hover:bg-violet-700 text-white rounded-full">
                <a href="#kontakt" onClick={() => setMobileMenuOpen(false)}>{t.nav.cta}</a>
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {mobileMenuOpen && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setMobileMenuOpen(false)} />}

      {/* Hero Section */}
      <section id="top" className="hero-bg pt-24 pb-16 sm:pt-28 sm:pb-20 md:pt-40 md:pb-32" data-testid="hero-section">
        <div className="container-main relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-2 sm:px-4 rounded-full bg-violet-500/10 border border-violet-500/20 mb-6 sm:mb-8 animate-fade-in">
              <Zap className="w-4 h-4 text-violet-400" />
              <span className="text-xs sm:text-sm text-violet-300">{t.hero.badge}</span>
            </div>

            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-5 sm:mb-6 animate-fade-in-up font-['Space_Grotesk']">
              <span className="gradient-text">{t.hero.headline}</span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-zinc-400 mb-8 sm:mb-10 max-w-2xl mx-auto animate-fade-in-up delay-100">
              {t.hero.subheadline}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-12 sm:mb-16 animate-fade-in-up delay-200">
              <Button asChild size="lg"
                className="w-full sm:w-auto bg-violet-600 hover:bg-violet-700 text-white rounded-full px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg btn-scale animate-pulse-glow"
                data-testid="hero-cta-primary">
                <a href="#kontakt">
                  {t.hero.ctaPrimary}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline"
                className="w-full sm:w-auto border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white rounded-full px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg btn-scale"
                data-testid="hero-cta-secondary">
                <a href={language === "de" ? "#leistungen" : "#branchen"}>{t.hero.ctaSecondary}</a>
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-10 sm:mb-12 animate-fade-in-up delay-300">
              {[
                { icon: DollarSign, text: t.hero.benefit1 },
                { icon: Zap, text: t.hero.benefit2 },
                { icon: Clock, text: t.hero.benefit3 },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center justify-center gap-3 p-3 sm:p-4 rounded-xl bg-zinc-900/50 border border-zinc-800">
                  <item.icon className="w-5 h-5 text-violet-400 shrink-0" />
                  <span className="text-sm sm:text-base text-zinc-300 font-medium">{item.text}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row sm:flex-wrap items-center justify-center gap-3 sm:gap-4 text-sm text-zinc-500 animate-fade-in delay-400">
              <span className="flex items-center gap-2"><Users className="w-4 h-4" />{t.hero.trust1}</span>
              <span className="hidden sm:inline">•</span>
              <span className="flex items-center gap-2"><Shield className="w-4 h-4" />{t.hero.trust2}</span>
              <span className="hidden sm:inline">•</span>
              <span className="flex items-center gap-2"><Clock className="w-4 h-4" />{t.hero.trust3}</span>
            </div>
          </div>
        </div>
      </section>

      {language === "de" ? (
        <section id="leistungen" className="py-16 md:py-28 bg-zinc-900/30 reveal-on-scroll" ref={addToRefs}>
          <div className="container-main">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-white font-['Space_Grotesk']">
                Leistungen für den deutschen Mittelstand
              </h2>
              <p className="text-zinc-400 max-w-3xl mx-auto">
                Diese öffentlichen Seiten beschreiben die wichtigsten Automatisierungsfelder, die wir aktuell für
                Telefon, E-Mail, Dokumente und operative Workflows umsetzen.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {mainServiceLinks.map((page) => (
                <a
                  key={page.path}
                  href={page.path}
                  className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5 sm:p-6 hover:border-violet-500/40 transition-colors"
                >
                  <p className="text-sm text-violet-300 mb-2">Leistung</p>
                  <h3 className="text-xl sm:text-2xl font-semibold text-white mb-3 font-['Space_Grotesk']">{page.h1}</h3>
                  <p className="text-zinc-400 mb-4">{page.description}</p>
                  <span className="inline-flex items-center gap-2 text-violet-300">
                    Mehr zu {page.h1}
                    <ChevronRight className="w-4 h-4" />
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Benefits Section */}
      <section id="vorteile" className="py-16 md:py-32 reveal-on-scroll" ref={addToRefs} data-testid="vorteile-section">
        <div className="container-main">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 font-['Space_Grotesk']">
              {t.benefits.title} <span className="gradient-text">{t.benefits.titleHighlight}</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">{t.benefits.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.benefits.items.map((benefit, idx) => {
              const Icon = benefitIcons[idx];
              return (
                <div key={idx} className="feature-card hover-card-effect" style={{ animationDelay: `${idx * 0.1}s` }}>
                  <div className="icon-container mb-4">
                    <Icon className="w-6 h-6 text-violet-400" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-white font-['Space_Grotesk']">{benefit.title}</h3>
                  <p className="text-zinc-400">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {language === "de" ? (
        <section id="branchen-links" className="py-20 md:py-28 bg-zinc-900/30 reveal-on-scroll" ref={addToRefs}>
          <div className="container-main">
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-white font-['Space_Grotesk']">
                Branchen mit hohem Automatisierungspotenzial
              </h2>
              <p className="text-zinc-400 max-w-3xl mx-auto">
                Drei typische Einsatzfelder, in denen Telefon, E-Mail, Dokumente und Workflows besonders oft zum
                Engpass werden.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {industryLinks.map((page) => (
                <a
                  key={page.path}
                  href={page.path}
                  className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-6 hover:border-violet-500/40 transition-colors"
                >
                  <p className="text-sm text-violet-300 mb-2">Branche</p>
                  <h3 className="text-2xl font-semibold text-white mb-3 font-['Space_Grotesk']">{page.h1}</h3>
                  <p className="text-zinc-400 mb-4">{page.description}</p>
                  <span className="inline-flex items-center gap-2 text-violet-300">
                    Branchenlösung ansehen
                    <ChevronRight className="w-4 h-4" />
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Functions Section */}
      <section id="funktionen" className="py-16 md:py-32 bg-zinc-900/30 reveal-on-scroll" ref={addToRefs} data-testid="funktionen-section">
        <div className="container-main">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 font-['Space_Grotesk']">
              {t.functions.title} <span className="gradient-text">{t.functions.titleHighlight}</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">{t.functions.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
            {t.functions.items.map((func, idx) => {
              const Icon = functionIcons[idx];
              return (
                <div key={idx} className="p-4 sm:p-5 rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-violet-500/30 transition-all duration-300 hover:-translate-y-1">
                  <Icon className="w-8 h-8 text-violet-400 mb-3" />
                  <h3 className="text-lg font-semibold mb-2 text-white font-['Space_Grotesk']">{func.title}</h3>
                  <p className="text-sm text-zinc-400">{func.description}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <p className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-violet-500/10 border border-violet-500/20 text-sm text-violet-300">
              <Shield className="w-4 h-4" />{t.functions.hint}
            </p>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="branchen" className="py-16 md:py-32 reveal-on-scroll" ref={addToRefs} data-testid="branchen-section">
        <div className="container-main">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 font-['Space_Grotesk']">
              {t.industries.title} <span className="gradient-text">{t.industries.titleHighlight}</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">{t.industries.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.industries.items.map((industry, idx) => {
              const Icon = industryIcons[idx];
              return (
                <div key={idx} className="industry-card">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-violet-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white font-['Space_Grotesk']">{industry.title}</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {industry.examples.map((example, i) => (
                      <span key={i} className="px-3 py-1 text-sm rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700">{example}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="ablauf" className="py-16 md:py-32 bg-zinc-900/30 reveal-on-scroll" ref={addToRefs} data-testid="ablauf-section">
        <div className="container-main">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 font-['Space_Grotesk']">
              {t.process.title} <span className="gradient-text">{t.process.titleHighlight}</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">{t.process.subtitle}</p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="relative">
              {t.process.steps.map((step, idx) => (
                <div key={idx} className="relative pl-16 sm:pl-20 pb-10 sm:pb-12 last:pb-0">
                  {idx < t.process.steps.length - 1 && (
                    <div className="absolute left-5 sm:left-6 top-10 sm:top-12 bottom-0 w-0.5 bg-gradient-to-b from-violet-500 to-violet-500/20" />
                  )}
                  <div className="timeline-dot absolute left-0 top-0">{idx + 1}</div>
                  <div className="pt-2">
                    <h3 className="text-lg sm:text-xl font-semibold text-white mb-2 font-['Space_Grotesk']">{step.title}</h3>
                    <p className="text-zinc-400">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg"
              className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-8 btn-scale" data-testid="ablauf-cta">
              <a href="#kontakt">
                {t.process.cta}
                <ChevronRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="referenzen" className="py-16 md:py-32 reveal-on-scroll" ref={addToRefs} data-testid="referenzen-section">
        <div className="container-main">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 font-['Space_Grotesk']">
              <span className="gradient-text">{t.testimonials.title}</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">{t.testimonials.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.testimonials.items.map((testimonial, idx) => (
              <div key={idx} className="testimonial-card">
                <Quote className="w-8 h-8 text-violet-500/30 mb-4" />
                <p className="text-lg text-white mb-6 font-medium">"{testimonial.quote}"</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-zinc-400">{testimonial.name}</p>
                    <p className="text-xs text-zinc-500">{testimonial.company}</p>
                  </div>
                  <span className="px-3 py-1 text-xs rounded-full bg-violet-500/10 text-violet-300 border border-violet-500/20">{testimonial.result}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section id="sicherheit" className="py-16 md:py-32 bg-zinc-900/30 reveal-on-scroll" ref={addToRefs} data-testid="sicherheit-section">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 font-['Space_Grotesk']">
                {t.security.title} <span className="gradient-text">{t.security.titleHighlight}</span>
              </h2>
              <p className="text-zinc-400">{t.security.subtitle}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {t.security.items.map((item, idx) => {
                const Icon = securityIcons[idx];
                return (
                  <div key={idx} className="security-badge">
                    <Icon className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-zinc-300">{item}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 md:py-32 reveal-on-scroll" ref={addToRefs} data-testid="faq-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 font-['Space_Grotesk']">
                {t.faq.title} <span className="gradient-text">{t.faq.titleHighlight}</span>
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {t.faq.items.map((faq, idx) => (
                <AccordionItem key={idx} value={`item-${idx}`}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl px-4 sm:px-6 data-[state=open]:border-violet-500/30">
                  <AccordionTrigger className="text-left text-white hover:text-violet-300 font-['Space_Grotesk'] font-medium py-4 sm:py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-zinc-400 pb-4 sm:pb-5">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="kontakt" className="py-16 md:py-32 bg-zinc-900/30 reveal-on-scroll" ref={addToRefs} data-testid="kontakt-section">
        <div className="container-main">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 font-['Space_Grotesk']">
                <span className="gradient-text">{t.contact.title}</span>
              </h2>
              <p className="text-zinc-400">{t.contact.subtitle}</p>
            </div>

            {formSubmitted ? (
              <div className="text-center p-12 rounded-2xl bg-zinc-900/50 border border-emerald-500/30">
                <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                <h3 className="text-2xl font-semibold text-white mb-2 font-['Space_Grotesk']">{t.contact.success.title}</h3>
                <p className="text-zinc-400">{t.contact.success.message}</p>
              </div>
            ) : (
              <form onSubmit={handleFormSubmit} className="p-5 sm:p-6 md:p-10 rounded-2xl bg-zinc-900/50 border border-zinc-800 glow-violet-subtle">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 mb-6">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">{t.contact.form.name} *</label>
                    <Input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="form-input bg-zinc-800/50 border-zinc-700 focus:border-violet-500"
                      placeholder={t.contact.form.namePlaceholder} data-testid="form-name" />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">{t.contact.form.email} *</label>
                    <Input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="form-input bg-zinc-800/50 border-zinc-700 focus:border-violet-500"
                      placeholder={t.contact.form.emailPlaceholder} data-testid="form-email" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 mb-6">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">{t.contact.form.company}</label>
                    <Input type="text" value={formData.firma} onChange={(e) => setFormData({ ...formData, firma: e.target.value })}
                      className="form-input bg-zinc-800/50 border-zinc-700 focus:border-violet-500"
                      placeholder={t.contact.form.companyPlaceholder} data-testid="form-firma" />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">{t.contact.form.industry}</label>
                    <Select value={formData.branche} onValueChange={(value) => setFormData({ ...formData, branche: value })}>
                      <SelectTrigger className="form-input bg-zinc-800/50 border-zinc-700 focus:border-violet-500" data-testid="form-branche">
                        <SelectValue placeholder={t.contact.form.industryPlaceholder} />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {Object.entries(t.contact.form.industries).map(([key, value]) => (
                          <SelectItem key={key} value={key}>{value}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm text-zinc-400 mb-2">{t.contact.form.process}</label>
                  <Textarea value={formData.prozess} onChange={(e) => setFormData({ ...formData, prozess: e.target.value })}
                    className="form-input bg-zinc-800/50 border-zinc-700 focus:border-violet-500 min-h-[120px]"
                    placeholder={t.contact.form.processPlaceholder} data-testid="form-prozess" />
                </div>

                <div className="flex items-start gap-3 mb-8">
                  <Checkbox id="consent" checked={formData.consent}
                    onCheckedChange={(checked) => setFormData({ ...formData, consent: checked })}
                    className="mt-1 border-zinc-600 data-[state=checked]:bg-violet-600" data-testid="form-consent" />
                  <label htmlFor="consent" className="text-sm text-zinc-400 cursor-pointer">{t.contact.form.consent} *</label>
                </div>

                <Button type="submit" size="lg" disabled={!formData.consent || formLoading}
                  className="w-full bg-violet-600 hover:bg-violet-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white rounded-full py-5 sm:py-6 text-base sm:text-lg btn-scale"
                  data-testid="form-submit">
                  {formLoading ? (language === 'de' ? 'Wird gesendet...' : 'Sending...') : t.contact.form.submit}
                  {!formLoading && <ArrowRight className="ml-2 w-5 h-5" />}
                </Button>
                {formError && (
                  <p className="text-red-400 text-sm mt-3 text-center" data-testid="form-error">{formError}</p>
                )}
              </form>
            )}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-12 md:py-24">
        <div className="container-main">
          <div className="cta-section p-6 sm:p-8 md:p-12 text-center relative z-10">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-white font-['Space_Grotesk']">{t.ctaBanner.title}</h2>
            <p className="text-zinc-400 mb-6 sm:mb-8 max-w-xl mx-auto">{t.ctaBanner.subtitle}</p>
            <Button asChild size="lg"
              className="w-full sm:w-auto bg-white text-violet-900 hover:bg-zinc-100 rounded-full px-6 sm:px-8 btn-scale" data-testid="cta-banner-button">
              <a href="#kontakt">
                {t.ctaBanner.cta}
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-zinc-800" data-testid="footer">
        <div className="container-main">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <ScandicDevLogo className="h-14 w-auto object-contain" />

            <nav className="flex flex-wrap justify-center gap-5 text-sm text-zinc-500">
              {navLinks.map((link) => (
                <a key={link.href} href={link.href}
                  className="hover:text-white transition-colors">
                  {link.label}
                </a>
              ))}
            </nav>

            <div className="flex flex-wrap justify-center gap-4 text-sm text-zinc-500">
              <a href={getLegalPath("impressum", language)} className="hover:text-white transition-colors">{t.footer.impressum}</a>
              <span className="hidden sm:inline">|</span>
              <a href={getLegalPath("datenschutz", language)} className="hover:text-white transition-colors">{t.footer.datenschutz}</a>
              <span className="hidden sm:inline">|</span>
              <a href={getLegalPath("ki-ethik", language)} className="hover:text-white transition-colors">{t.footer.aiEthics}</a>
            </div>

            <p className="text-sm text-zinc-500">© {new Date().getFullYear()} ScandicDev</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
